import React from 'react';
import PropTypes from 'prop-types';
import './SeverityIndicator.css';

/**
 * SeverityIndicator Component
 * 
 * Displays medical severity levels with color-coded dots and text labels.
 * Provides instant visual recognition through color while maintaining accessibility.
 * 
 * @param {string} severity - The severity level (e.g., "Contraindicated", "Severe", "Moderate", "Minor")
 * @param {string} size - Size variant: "sm" (default for tables) or "md"
 * @param {boolean} showLabel - Whether to show text label (default: true)
 * @param {string} className - Additional CSS classes
 */
const SeverityIndicator = ({ 
  severity, 
  size = 'sm', 
  showLabel = true,
  className = '' 
}) => {
  // Normalize severity string and map to config
  const normalizedSeverity = severity?.trim() || '';
  
  // Configuration for severity levels with colors and display labels
  const severityConfig = {
    // Contraindicated variants
    'Contraindicated Drug Combination': { 
      color: 'severity-red', 
      label: 'Contraindicated',
      description: 'Drug combination is contraindicated'
    },
    'Contraindicated': { 
      color: 'severity-red', 
      label: 'Contraindicated',
      description: 'Contraindicated for use'
    },
    'Contraindication': { 
      color: 'severity-red', 
      label: 'Contraindication',
      description: 'Contraindication for use'
    },
    'Absolute Contraindication': { 
      color: 'severity-red', 
      label: 'Contraindicated',
      description: 'Absolute contraindication'
    },
    
    // Severe variants
    'Severe Warning': { 
      color: 'severity-orange', 
      label: 'Severe Warning',
      description: 'Severe warning'
    },
    'Severe Interaction': { 
      color: 'severity-orange', 
      label: 'Severe',
      description: 'Severe interaction risk'
    },
    'Severe': { 
      color: 'severity-orange', 
      label: 'Severe',
      description: 'Severe severity level'
    },
    'Known Interaction': { 
      color: 'severity-orange', 
      label: 'Severe',
      description: 'Known severe interaction'
    },
    
    // Moderate variants
    'Moderate Interaction': { 
      color: 'severity-amber', 
      label: 'Moderate',
      description: 'Moderate interaction risk'
    },
    'Moderate': { 
      color: 'severity-amber', 
      label: 'Moderate',
      description: 'Moderate severity level'
    },
    
    // Minor variants
    'Minor': { 
      color: 'severity-green', 
      label: 'Minor',
      description: 'Minor severity level'
    },
    'Minor Interaction': { 
      color: 'severity-green', 
      label: 'Minor',
      description: 'Minor interaction risk'
    },
    
    // Undetermined/Unknown variants
    'Undetermined Severity': { 
      color: 'severity-gray', 
      label: 'Undetermined',
      description: 'Severity level undetermined'
    },
    'Undetermined': { 
      color: 'severity-gray', 
      label: 'Undetermined',
      description: 'Undetermined severity'
    },
    'Unknown': { 
      color: 'severity-gray', 
      label: 'Unknown',
      description: 'Unknown severity level'
    }
  };
  
  // Get config or fallback to default
  const config = severityConfig[normalizedSeverity] || { 
    color: 'severity-gray', 
    label: normalizedSeverity || 'Unknown',
    description: normalizedSeverity
  };
  
  return (
    <div 
      className={`severity-indicator severity-${size} ${className}`}
      title={config.description}
      role="status"
      aria-label={`Severity: ${config.label}`}
    >
      <span 
        className={`severity-dot ${config.color}`}
        aria-hidden="true"
      />
      {showLabel && (
        <span className="severity-label">
          {config.label}
        </span>
      )}
    </div>
  );
};

SeverityIndicator.propTypes = {
  severity: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
  showLabel: PropTypes.bool,
  className: PropTypes.string,
};

export default SeverityIndicator;
